// Updated for pixel-perfect match to original HTML and CSS
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './MainPage.css';
import { Button, AvatarDropdown } from '../../design-system';
import { LocationOverlay } from '../LocationOverlay/LocationOverlay';
import { isSelectLocationPath } from '../../utils/locationChoice';
import { Store } from '../../types/location.types';
import MenuOverlay from '../MenuOverlay/MenuOverlay';
import { optimizedApiService } from '../../config/api.config';
import StoreOpeningHours from '../StoreOpeningHours/StoreOpeningHours';
import SEO from '../SEO';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { useSEO } from '../../context/SEOContext';
import { storeService } from '../../services/store.service';
import BookingsClosedCard from '../BookingsClosed/BookingsClosedCard';
import PromotionBar from '../Promotions/PromotionBar';

interface UserProfile {
  name: string;
  email: string;
  picture: string;
}

interface MainPageProps {
  onContinue: (petType: 'dog' | 'cat') => void;
  onMenuOpen: () => void;
  onBooking: () => void;
  selectedStore: Store | null;
  onLocationSelect: (store: Store) => void;
  user: UserProfile | null;
  onLogin: (profile: UserProfile) => void;
  onLogout: () => void;
  onLoginError: (error: any) => void;
  storeCount?: number;
  companyLogoUrl?: string | null;
  onLoginClick?: () => void;
}

const MainPage: React.FC<MainPageProps> = ({
  onContinue,
  onMenuOpen,
  onBooking,
  selectedStore,
  onLocationSelect,
  user,
  onLogin,
  onLogout,
  onLoginError,
  storeCount = 0,
  companyLogoUrl,
  onLoginClick,
}) => {
  const navigate = useNavigate();
  const [selectedPet, setSelectedPet] = useState<'dog' | 'cat' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [offersShown, setOffersShown] = useState(false);
  const [locationOverlayOpen, setLocationOverlayOpen] = useState(false);
  const [hasShownLocationOverlay, setHasShownLocationOverlay] = useState(false);
  const topNavRef = useRef<HTMLElement>(null);
  const mainContentRef = useRef<HTMLElement>(null);

  // Pet type availability state
  const [petTypeAvailability, setPetTypeAvailability] = useState<{ dog: boolean; cat: boolean }>({ dog: true, cat: true });

  // Derived state: check if only one pet type is available
  const onlyDogAvailable = petTypeAvailability.dog && !petTypeAvailability.cat;
  const onlyCatAvailable = petTypeAvailability.cat && !petTypeAvailability.dog;
  const singlePetTypeOnly = onlyDogAvailable || onlyCatAvailable;
  const availablePetType: 'dog' | 'cat' | null = onlyDogAvailable ? 'dog' : onlyCatAvailable ? 'cat' : null;

  useEffect(() => {
    // Check if there's a stored store in localStorage
    const storedStore = localStorage.getItem('selectedStore');
    const hasStoredStore = storedStore && JSON.parse(storedStore);

    // Only automatically show location overlay if:
    // 1. No store is selected AND
    // 2. No store stored in localStorage AND
    // 3. Haven't shown overlay before AND
    // 4. There is MORE than 1 store (single store should be auto-selected, no overlay needed)
    // This applies to BOTH logged-in and non-logged-in users
    // The salon's link /select-location (D28, G49) always asks, nothing
    // ticked, whatever this phone remembers.
    const onLink = isSelectLocationPath(window.location.pathname);
    const shouldShowLocationOverlay =
      (onLink || (!selectedStore && !hasStoredStore)) && !hasShownLocationOverlay && storeCount > 1;


    if (shouldShowLocationOverlay) {
      setLocationOverlayOpen(true);
      setHasShownLocationOverlay(true);
    }
  }, [selectedStore, hasShownLocationOverlay, storeCount]);

  // Check pet type availability when store changes
  useEffect(() => {
    const checkPetTypeAvailability = async () => {
      if (!selectedStore?.id) {
        // Default to both available if no store selected
        setPetTypeAvailability({ dog: true, cat: true });
        return;
      }

      try {
        const availability = await optimizedApiService.checkCategoryAvailability(selectedStore.id);
        setPetTypeAvailability({ dog: availability.dog, cat: availability.cat });
      } catch (error) {
        console.error('Error checking pet type availability:', error);
        // Default to both available on error
        setPetTypeAvailability({ dog: true, cat: true });
      }
    };

    checkPetTypeAvailability();
  }, [selectedStore?.id]);

  // Measure top-nav height and apply to main-content
  useEffect(() => {
    const updateMainContentMargin = () => {
      if (topNavRef.current && mainContentRef.current) {
        const topNavHeight = topNavRef.current.offsetHeight;
        mainContentRef.current.style.marginTop = `${topNavHeight}px`;
      }
    };

    // Initial measurement
    updateMainContentMargin();

    // Update on resize
    window.addEventListener('resize', updateMainContentMargin);

    // Use ResizeObserver to watch for changes in top-nav height
    let resizeObserver: ResizeObserver | null = null;
    if (topNavRef.current) {
      resizeObserver = new ResizeObserver(updateMainContentMargin);
      resizeObserver.observe(topNavRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateMainContentMargin);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [storeCount]); // Re-run when storeCount changes (store selector appears/disappears)

  const handleSelectPet = (type: 'dog' | 'cat') => {
    setSelectedPet(type === selectedPet ? null : type); // Toggle selection
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Only deselect if clicking the content-section area directly, not its children
    if (e.target === e.currentTarget) {
      setSelectedPet(null);
    }
  };

  const handleContinue = () => {
    if (selectedPet) {
      onContinue(selectedPet);
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLocationSelect = (location: Store) => {
    onLocationSelect(location);  // Call the parent handler
    setLocationOverlayOpen(false);  // Close the overlay
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLogin = (profile: UserProfile) => {
    onLogin(profile);
    setMenuOpen(false); // Close menu after login
  };

  const handleLogout = () => {
    onLogout();
    setMenuOpen(false); // Close menu after logout
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLoginError = (error: any) => {
    console.error('Login failed:', error);
    // You might want to show an error message to the user here
  };

  // Get company data from context
  const { companyName, companyLogo } = useSEO();
  // Whether this location takes online bookings (mpgadmin D17: the plan
  // has ended, the location is parked, or payouts aren't set up all say
  // no). When it doesn't, the page offers a call instead of a booking.
  const acceptingBookings = storeService.canBookOnline(selectedStore);

  // Extract location from store address
  const shopLocation = selectedStore?.address?.formattedAddress
    || selectedStore?.address?.addressLine1
    || selectedStore?.address?.street
    || undefined;

  // LocalBusiness structured data for SEO (per requirements)
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": companyName,
    "image": companyLogo || companyLogoUrl || undefined,
    "url": window.location.origin,
    "@id": `${window.location.origin}/#organization`,
    "priceRange": "$$",
    "makesOffer": {
      "@type": "Offer",
      "offeredBy": {
        "@type": "Brand",
        "name": "My Pet Groomer"
      }
    },
    ...(selectedStore && {
      "address": {
        "@type": "PostalAddress",
        "streetAddress": selectedStore.address?.street || selectedStore.address?.addressLine1,
        "addressLocality": selectedStore.address?.formattedAddress
      }
    })
  };

  return (
    <>
      <SEO
        shopName={companyName}
        shopLocation={shopLocation}
        ogImage={companyLogo || companyLogoUrl || undefined}
        structuredData={localBusinessSchema}
      />
    <div className={`container main-page-background${selectedStore ? ' has-store-selector' : ''}`}>
      {/* Top Navigation */}
      <nav className="top-nav" ref={topNavRef}>
        <div className="top-nav-content">
          <div className="nav-left">
            <Button variant="menu" onClick={toggleMenu}>
              <img src="/assets/menu.svg" alt="Menu" />
            </Button>
          </div>
          {companyLogoUrl && (
            <div className="logo-container" onClick={() => window.location.reload()} style={{ cursor: 'pointer' }}>
              <img src={companyLogoUrl} alt="Logo" />
            </div>
          )}
          <div className="nav-right">
            <LanguageSwitcher variant="nav" />
            {user && (
              <div className="profile-icon">
                <AvatarDropdown
                  user={user}
                  onLogout={handleLogout}
                  size="medium"
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Store Selector Row - Show when store is selected */}
        {/* If multiple stores: clickable with chevron. If single store: display only, no chevron */}
        {selectedStore && (
          <div className="store-selector-row">
            {storeCount > 1 ? (
              <button
                className="store-selector-button"
                onClick={() => setLocationOverlayOpen(true)}
                type="button"
              >
                <img
                  src="/assets/groomingshop.png"
                  alt=""
                  className="store-icon"
                />
                <div className="store-info">
                  <span className="store-label">Booking at</span>
                  <span className="store-name">{selectedStore.name}</span>
                </div>
                <img src="/assets/down.svg" alt="" className="store-chevron" />
              </button>
            ) : (
              <div className="store-selector-display">
                <img
                  src="/assets/groomingshop.png"
                  alt=""
                  className="store-icon"
                />
                <div className="store-info">
                  <span className="store-label">Booking at</span>
                  <span className="store-name">{selectedStore.name}</span>
                </div>
              </div>
            )}
            {selectedStore.businessHours && selectedStore.businessHours.length > 0 && (
              <div className="store-opening-hours-container">
                <StoreOpeningHours businessHours={selectedStore.businessHours} />
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <MenuOverlay
          onClose={() => setMenuOpen(false)}
          onBooking={onBooking}
          user={user}
          onLogin={onLogin}
          onLogout={onLogout}
          onLoginError={onLoginError}
          selectedStore={selectedStore}
        />
      )}

      {/* Main Content */}
      <main className={`main-content${offersShown ? ' main-content--offers' : ''}`} ref={mainContentRef}>
        {/*
          * The salon's offers (SF 01, GUARDRAILS G9): floating above the dog on
          * a phone; on a wide screen part of the page, centred above the heading.
          */}
        <PromotionBar bottom={150} storeName={selectedStore?.name} home onShown={setOffersShown} />
        {!acceptingBookings ? (
          /*
           * Not "a notice above the booking flow": the flow is gone. The
           * notice used to sit over "Step 1 · Choose the pet type" and "skip
           * this step and book now", inviting a booking it said wasn't
           * possible, with no number to call.
           */
          <BookingsClosedCard
            name={selectedStore?.name || companyName || 'This salon'}
            storeId={selectedStore?.id}
            address={shopLocation}
            signedIn={!!user}
            onSignIn={() => onLoginClick?.()}
            onAppointments={() => navigate('/appointments')}
          />
        ) : (
        <div className="content-section" onClick={handleBackgroundClick}>
          <h1>We help you to find the right services for <span className="highlight-text">your pet</span>.</h1>
          <div className="pet-selection-card" onClick={(e) => e.stopPropagation()}>
            <div className="step-label-1">
              <span className="step-number">Step 1</span>
              <span className="step-text">
                {singlePetTypeOnly ? "Let's create a profile" : 'Choose the pet type'}
              </span>
            </div>
            {singlePetTypeOnly ? (
              /* Single pet type available - show only that pet */
              <div className="pet-options single-pet-mode">
                <div className="pet-option selected">
                  <div className="checkmark">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="pet-image">
                    <img
                      src={availablePetType === 'dog' ? '/assets/dog-icon.svg' : '/assets/cat-icon.svg'}
                      alt={availablePetType === 'dog' ? 'Dog' : 'Cat'}
                    />
                  </div>
                  <span>{availablePetType === 'dog' ? 'Dog' : 'Cat'}</span>
                </div>
              </div>
            ) : (
              /* Both pet types available - show selection */
              <div className="pet-options">
                <div
                  className={`pet-option${selectedPet === 'dog' ? ' selected' : ''}`}
                  onClick={() => handleSelectPet('dog')}
                >
                  <div className="checkmark">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="pet-image">
                    <img src="/assets/dog-icon.svg" alt="Dog" />
                  </div>
                  <span>Dog</span>
                </div>
                <div
                  className={`pet-option${selectedPet === 'cat' ? ' selected' : ''}`}
                  onClick={() => handleSelectPet('cat')}
                >
                  <div className="checkmark">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="pet-image">
                    <img src="/assets/cat-icon.svg" alt="Cat" />
                  </div>
                  <span>Cat</span>
                </div>
              </div>
            )}
          </div>
          {singlePetTypeOnly ? (
            /* Single pet type - always show CTA, auto-select on click */
            <Button
              variant="primary"
              isEnabled={true}
              onClick={() => onContinue(availablePetType!)}
            >
              Let's start
            </Button>
          ) : selectedPet && (
            /* Both types - show CTA only when selected */
            <Button
              variant="primary"
              isEnabled={selectedPet !== null}
              onClick={handleContinue}
            >
              Continue to create a pet profile
            </Button>
          )}
          <div onClick={(e) => e.stopPropagation()}>
            Or <Button variant="skip" onClick={onBooking}>skip this step and book now</Button>
          </div>
        </div>
        )}
      </main>

      {/* Copyright Footer */}
      <footer className="storefront-footer">
        <p className="footer-copyright">
          &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
        </p>
        <p className="footer-powered-by">
          Powered by{' '}
          <a href="https://www.mypetgroomer.com.au" target="_blank" rel="noopener noreferrer">
            My Pet Groomer®
          </a>
        </p>
      </footer>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        {/* Dog illustration background */}
        <div className="bottom-nav-dog-bg">
          <img src="/assets/dog_body.svg" alt="" aria-hidden="true" />
        </div>
        <div className="nav-buttons-container">
        {user ? (
            <button type="button" className="nav-button" onClick={() => navigate('/appointments')}>Appointments</button>
          ) : (
            <button type="button" className="nav-button" onClick={onLoginClick}>
              Sign in
            </button>
          )}
          {acceptingBookings && (
            <button type="button" className="nav-button" onClick={onBooking}>Book now</button>
          )}

        </div>
      </nav>
    </div>

    <LocationOverlay 
      isOpen={locationOverlayOpen}
      onClose={() => setLocationOverlayOpen(false)}
      onLocationSelect={handleLocationSelect}
    />
    </>
  );
};

export default MainPage; 